import { useState } from 'react';
import { Button, Grid } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

import { unitsApiKeyStore } from '../../api/units-api-key-store';
import { officeApiKeyStore } from '../../api/office-api-key-store';
import { projectsApiKeyStore } from '../../api/projects-api-key-store';
import { customersApiKeyStore } from '../../api/customers-api-key-store';
import { workOrdersApiKeyStore } from '../../api/workorders-api-key-store';
import { servicesApiKeyStore } from '../../api/masters/services-api-key-store';
import { FormAutoCompleteSelectDynamic } from '../../components/hook-form/form-autocomplete-select.dynamic.component';
import { FormAutoCompleteMultiSelectDynamic } from '../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { FormCheckbox } from '../../components/hook-form/form-checkbox.component';
import { serviceGroupApiKeyStore } from '../../api/masters/service-group-api-key-store';

interface WorkOrderFilters {
  hanldeChangeWorkOrderID: () => void;
}

const TEMP_DATA = [
  {
    value: 'all',
    label: 'All',
    isDefault: true,
  },
  {
    value: 'others',
    label: 'Original Inspection',
    isDefault: false,
  },
  {
    value: 'retest',
    label: 'Retest Inspection',
    isDefault: false,
  },
];

const isDUXEnabled = process.env.REACT_APP_DUX_FLAG === 'true';

export const WorkOrderFilters = ({ hanldeChangeWorkOrderID }: WorkOrderFilters) => {
  const { setValue, getValues } = useFormContext();
  const [officeDisabled, setOfficeDisabled] = useState<boolean>(true);
  const [projectDisabled, setProjectDisabled] = useState<boolean>(true);
  const [unitDisabled, setUnitDisabled] = useState<boolean>(true);

  const handleCustomerChange = (value: any) => {
    setOfficeDisabled(!value);
    setProjectDisabled(true);
    setUnitDisabled(true);
    setValue('office', { officeID: '', officeName: '' });
    setValue('project', { projectID: '', projectName: '' });
    setValue('unit', { unitID: '', unitName: '' });
    setValue('jobsite', { unitID: '', jobsite: '' });
    setValue('workOrderStatusIDs', null);
    setValue('service', null);
    setValue('serviceGroupName', null);
    hanldeChangeWorkOrderID();
  };

  const handleOfficeChange = (value: any) => {
    setProjectDisabled(!value);
    setUnitDisabled(true);
    setValue('project', { projectID: '', projectName: '' });
    setValue('unit', { unitID: '', unitName: '' });
    setValue('jobsite', { unitID: '', jobsite: '' });
    setValue('serviceGroupName', null);
    hanldeChangeWorkOrderID();
  };

  const handleProjectChange = (value: any) => {
    setUnitDisabled(!value);
    setValue('unit', { unitID: '', unitName: '' });
    setValue('jobsite', { unitID: '', jobsite: '' });
    setValue('serviceGroupName', null);
    hanldeChangeWorkOrderID();
  };

  const handleJobsiteChange = (value: any) => {
    if (value) setValue('unit', { unitID: value?.unitID, unitName: value?.unitName });
    else setValue('unit', { unitID: '', unitName: '' });

    hanldeChangeWorkOrderID();
  };

  const handleStatusDrpdwn = (value: any) => {
    setValue('workOrderStatusIDs', null);
  };

  return (
    <Grid container spacing={1}>
      <Grid item lg={12} sm={12} xl={12} xs={12}>
        <Grid container spacing={1}>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="customer"
              label="Customer"
              onChange={handleCustomerChange}
              getQueryKeyConfig={() => customersApiKeyStore.getCustomers()}
              optionsValueKey="customerID"
              optionsLabelKey="customerName"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="office"
              label="Offices"
              disabled={!getValues('customer')?.customerID}
              watchKeys={['customer']}
              onChange={handleOfficeChange}
              getQueryKeyConfig={(inputData: any) =>
                officeApiKeyStore.getCustomerOffices(inputData.customer?.customerID)
              }
              optionsValueKey="officeID"
              optionsLabelKey="officeName"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="project"
              label="Projects"
              disabled={!getValues('office')?.officeID}
              watchKeys={['office']}
              onChange={handleProjectChange}
              getQueryKeyConfig={(inputData: any) => projectsApiKeyStore.getOfficeProjects(inputData.office?.officeID)}
              optionsValueKey="projectID"
              optionsLabelKey="projectName"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="jobsite"
              label="Jobsite"
              onChange={handleJobsiteChange}
              disabled={!getValues('project')?.projectID}
              watchKeys={['project']}
              getQueryKeyConfig={(inputData: any) => unitsApiKeyStore.getProjectUnits(inputData.project?.projectID)}
              optionsValueKey="unitID"
              optionsLabelKey="jobsite"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="unit"
              label="Lot/Unit"
              onChange={() => {
                hanldeChangeWorkOrderID();
              }}
              disabled={!getValues('project')?.projectID}
              watchKeys={['project']}
              getQueryKeyConfig={(inputData: any) => unitsApiKeyStore.getProjectUnits(inputData.project?.projectID)}
              optionsValueKey="unitID"
              optionsLabelKey="unitName"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>

            <FormAutoCompleteMultiSelectDynamic
              name="workOrderStatusIDs"
              label="Status"
              onChange={() => {
                hanldeChangeWorkOrderID();
              }}
              watchKeys={['requestedInspections']}
              getQueryKeyConfig={(inputData: any) =>
                workOrdersApiKeyStore.getWorkOrderStatus(
                  inputData.requestedInspections ? 'InspectionRequestReport' : 'inspection'
                )
              }
              optionsLabelKey="workOrderStatus"
              optionsValueKey="workOrderStatusID"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteMultiSelectDynamic
              name="service"
              label="Service"
              onChange={() => {
                hanldeChangeWorkOrderID();
              }}
              // disabled={!getValues('customer')?.customerID}
              getQueryKeyConfig={() => servicesApiKeyStore.getServices()}
              optionsLabelKey="serviceName"
              optionsValueKey="serviceID"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="retestService"
              label="Inspection Type"
              dataItems={TEMP_DATA}
              optionsLabelKey="label"
              optionsValueKey="value"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="serviceGroupName"
              label="Service Group"
              getQueryKeyConfig={() => serviceGroupApiKeyStore.getServiceGroups()}
              optionsLabelKey="serviceGroupName"
              optionsValueKey="serviceGroupID"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={4} xs={6}>
            <FormAutoCompleteSelectDynamic
              name="workOrderActiveStatus"
              label="Active/Inactive Work Order Status"
              dataItems={[
                { statusValue: 'all', statusLabel: 'All', isDefault: false },
                { statusValue: 'active', statusLabel: 'Active', isDefault: false },
                { statusValue: 'inactive', statusLabel: 'Inactive', isDefault: false }]}
              optionsValueKey="statusValue"
              optionsLabelKey="statusLabel"
            />
          </Grid>
          <Grid item xl={3} lg={3} sm={3} xs={6} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* <Grid sx={{ flexGrow: 1 }}> */}
            {isDUXEnabled && (
              <FormCheckbox label="Requested Inspections" name="requestedInspections" onChange={handleStatusDrpdwn} />
            )}
            {/* </Grid> */}
            <Button type="submit" title="Filter" variant="contained" startIcon={<FilterAltOutlinedIcon />}>
              Filter
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
